import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { create_append_sessions_claim } from "../../apis/helper";
import { Icon } from "../../inJoy-components/Icon/Icon";
import { generateJoinUrl } from "../../redux/qr/qr-utils";

interface EventCreationSuccessProps {
	eventId: string;
	eventName: string;
	onClose: () => void;
}

export const EventCreationSuccess: React.FC<EventCreationSuccessProps> = ({
	eventId,
	eventName,
	onClose,
}) => {
	const { t } = useTranslation();
	const [qrUrl, setQrUrl] = useState<string>("");
	const [loadingToken, setLoadingToken] = useState(true);
	const [copied, setCopied] = useState(false);

	useEffect(() => {
		let mounted = true;
		const fetchToken = async () => {
			try {
				setLoadingToken(true);
				const token = await create_append_sessions_claim(eventId);
				if (mounted) {
					const url = generateJoinUrl(eventId, token);
					setQrUrl(url);
				}
			} catch (error) {
				console.error("Failed to generate token", error);
				if (mounted) {
					setQrUrl(generateJoinUrl(eventId, null));
				}
			} finally {
				if (mounted) setLoadingToken(false);
			}
		};

		fetchToken();
		return () => {
			mounted = false;
		};
	}, [eventId]);

	const handleCopy = async () => {
		if (!qrUrl) return;
		try {
			await navigator.clipboard.writeText(qrUrl);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error("Failed to copy:", err);
		}
	};

	return (
		<div className="relative max-w-[400px] w-full rounded-[8px] bg-white">
			<div className="relative flex w-full flex-col items-center gap-[32px] overflow-clip rounded-[inherit] p-[24px]">
				{/* Close Button */}
				<div className="flex w-full shrink-0 items-center justify-end">
					<button
						onClick={onClose}
						className="flex shrink-0 items-center rounded-[8px] p-[12px] transition-colors hover:bg-gray-100"
						aria-label="Close modal"
					>
						<Icon iconName="cross_outline" size="Small" color="#6D6D6D" />
					</button>
				</div>

				{/* Success Message */}
				<div className="flex w-full shrink-0 flex-col items-center gap-[24px]">
					<div className="flex w-full shrink-0 flex-col items-center gap-[12px]">
						<Icon iconName="check_outline" size="XXLarge" color="#1EB44A" />
						<p className="w-full shrink-0 text-center text-[24px] font-medium leading-[1.2] tracking-[-0.5px] text-[#404040]">
							{t("events.create_dialog.success")}
						</p>
						<p className="w-full shrink-0 text-center text-[16px] font-normal leading-[1.4] tracking-[0.25px] text-[#404040]">
							{eventName}
						</p>
					</div>

					{/* Event Details Card */}
					{loadingToken && (
						<div className="flex w-full items-center justify-center rounded-[4px] bg-[#f5f5f5] px-[16px] py-[20px]">
							<p className="text-[16px] text-[#6d6d6d]">
								{t("events.create_dialog.generating_link")}
							</p>
						</div>
					)}
				</div>

				{/* Divider */}
				<div className="relative h-0 w-full shrink-0">
					<div className="absolute bottom-full left-0 right-0 top-0">
						<div className="absolute inset-[-1px_0_0_0]">
							<svg
								className="block size-full"
								fill="none"
								preserveAspectRatio="none"
								viewBox="0 0 352 1"
							>
								<line stroke="#DFDFDF" x2="352" y1="0.5" y2="0.5" />
							</svg>
						</div>
					</div>
				</div>

				{/* Copy URL Section */}
				{qrUrl && (
					<button
						onClick={handleCopy}
						className="relative h-[48px] w-full shrink-0 rounded-[8px] transition-colors hover:bg-gray-50"
					>
						<div className="flex size-full items-center justify-center gap-[16px] overflow-clip rounded-[inherit] px-[12px] py-[16px]">
							<p className="relative min-h-px min-w-px flex-[1_0_0] truncate text-center text-[16px] font-normal leading-[1.4] tracking-[0.25px] text-[#404040]">
								{copied ? t("events.create_dialog.copied") : qrUrl}
							</p>
							<Icon
								iconName={copied ? "check_outline" : "copy_outline"}
								size="Medium"
								color={copied ? "#1EB44A" : "#343330"}
							/>
						</div>
						<div
							aria-hidden="true"
							className="pointer-events-none absolute inset-0 rounded-[8px] border border-solid border-[#dfdfdf]"
						/>
					</button>
				)}
			</div>
			<div
				aria-hidden="true"
				className="pointer-events-none absolute inset-0 rounded-[8px] border border-solid border-[#d1d5dc] shadow-[0px_4px_12px_0px_rgba(0,0,0,0.08)]"
			/>
		</div>
	);
};
